import { useEffect, useMemo, useRef } from 'react';

/**
 * Ein Textfeld mit Einfärbung — für die Compose- und .env-Vorlagen.
 *
 * Gebaut als **Überzug**: unten ein `<pre>` mit den eingefärbten Zeichen, oben
 * ein `<textarea>` mit durchsichtiger Schrift und sichtbarem Textcursor. Beide
 * teilen sich Schriftart, Größe, Zeilenhöhe und Innenabstand aufs Pixel; weicht
 * eines davon ab, wandert der Cursor sichtbar von der Schrift weg.
 *
 * Kein CodeMirror, kein Monaco. Für zwei Textfelder wären das ein paar hundert
 * Kilobyte und eine Abhängigkeit, die man pflegen muss — hier reichen ein
 * Zerteiler und zwei übereinanderliegende Kästen.
 *
 * Die wichtigste Einfärbung ist die, die der Kopfkommentar der .env erklärt:
 * `{{…}}` setzt der PC beim Ausrollen ein, `${…}` löst Compose auf dem Host
 * auf. Zwei verschiedene Dinge, die gleich aussehen — hier haben sie
 * verschiedene Farben.
 */

export type Sprache = 'yaml' | 'env' | 'sql';

export interface ZeilenMarke {
  zeile: number;
  schwere: 'fehler' | 'hinweis';
  text: string;
}

/**
 * Die Wörter, die SQL zu SQL machen.
 *
 * Bewusst knapp: eine vollständige Liste würde bei jedem Postgres-Release
 * altern, und für das Lesen einer Abfrage reicht es, wenn das Gerüst hervortritt.
 */
const SQL_WOERTER = new Set(
  ('select from where join left right inner outer full on group by order having limit offset ' +
    'insert into values update set delete truncate create alter drop table index view as with ' +
    'and or not null is in exists between like ilike distinct union all case when then else end ' +
    'asc desc count sum avg min max coalesce returning begin commit rollback')
    .split(' '),
);

type Art =
  | 'kommentar'
  | 'schluessel'
  | 'text'
  | 'zahl'
  | 'variable'
  | 'platzhalter'
  | 'zeichen'
  | 'normal';

interface Stueck {
  art: Art;
  wert: string;
}

const FARBE: Record<Art, string> = {
  kommentar: 'text-slate-400 italic',
  schluessel: 'text-sky-700',
  text: 'text-emerald-700',
  zahl: 'text-fuchsia-700',
  // Die beiden Klammerarten sind der Kern: gleiche Form, verschiedene Herkunft.
  variable: 'text-amber-700 bg-amber-50 rounded-sm',
  platzhalter: 'text-violet-700 bg-violet-50 rounded-sm',
  zeichen: 'text-slate-400',
  normal: 'text-slate-700',
};

const FARBE_WORT = 'text-sky-700 font-medium';

/** Zerteilt den Wertteil einer Zeile — Klammern, Zeichenketten, Zahlen. */
function wertStuecke(text: string): Stueck[] {
  const raus: Stueck[] = [];
  let puffer = '';
  const schiebe = () => {
    if (puffer) {
      raus.push({ art: 'normal', wert: puffer });
      puffer = '';
    }
  };

  for (let i = 0; i < text.length; i++) {
    const rest = text.slice(i);

    // ${…} — löst Compose auf dem Host auf.
    const v = /^\$\{[^}]*\}|^\$[A-Za-z_][A-Za-z0-9_]*/.exec(rest);
    if (v) {
      schiebe();
      raus.push({ art: 'variable', wert: v[0] });
      i += v[0].length - 1;
      continue;
    }

    // {{…}} — setzt der PC beim Ausrollen ein.
    const pl = /^\{\{[^}]*\}\}/.exec(rest);
    if (pl) {
      schiebe();
      raus.push({ art: 'platzhalter', wert: pl[0] });
      i += pl[0].length - 1;
      continue;
    }

    // Zeichenketten in Anführungszeichen.
    const s = /^"(?:[^"\\]|\\.)*"|^'(?:[^'\\]|\\.)*'/.exec(rest);
    if (s) {
      schiebe();
      // Auch in Anführungszeichen stecken Klammern — die sollen ihre Farbe
      // behalten, sonst verschwindet gerade das, worauf es ankommt.
      const innen = wertStuecke(s[0].slice(1, -1));
      raus.push({ art: 'text', wert: s[0][0] });
      for (const st of innen) raus.push(st.art === 'normal' ? { art: 'text', wert: st.wert } : st);
      raus.push({ art: 'text', wert: s[0][0] });
      i += s[0].length - 1;
      continue;
    }

    const z = /^\b\d+(?:\.\d+)?\b/.exec(rest);
    if (z && !/[A-Za-z_-]$/.test(puffer)) {
      schiebe();
      raus.push({ art: 'zahl', wert: z[0] });
      i += z[0].length - 1;
      continue;
    }

    puffer += text[i];
  }
  schiebe();
  return raus;
}

/** Zerteilt eine Zeile. Der Aufbau unterscheidet sich je Sprache, der Rest nicht. */
/** SQL: Kommentare, Zeichenketten, Schluesselwoerter. */
function sqlStuecke(zeile: string): Stueck[] {
  const kommentar = zeile.indexOf('--');
  if (kommentar === 0) return [{ art: 'kommentar', wert: zeile }];

  const rumpf = kommentar > 0 ? zeile.slice(0, kommentar) : zeile;
  const rest = kommentar > 0 ? zeile.slice(kommentar) : '';

  const raus: Stueck[] = [];
  // Woerter, Zeichenketten und alles dazwischen getrennt halten.
  const teile = rumpf.split(/('(?:[^']|'')*'|"[^"]*"|\w+)/);
  for (const t of teile) {
    if (!t) continue;
    if (/^['"]/.test(t)) raus.push({ art: 'text', wert: t });
    else if (/^\w+$/.test(t)) {
      if (SQL_WOERTER.has(t.toLowerCase())) raus.push({ art: 'schluessel', wert: t });
      else if (/^\d+$/.test(t)) raus.push({ art: 'zahl', wert: t });
      else raus.push({ art: 'normal', wert: t });
    } else raus.push({ art: 'zeichen', wert: t });
  }
  if (rest) raus.push({ art: 'kommentar', wert: rest });
  return raus;
}

export function zeileZerteilen(zeile: string, sprache: Sprache): Stueck[] {
  if (sprache === 'sql') return sqlStuecke(zeile);
  const eingerueckt = /^(\s*)([\s\S]*)$/.exec(zeile)!;
  const rumpf = eingerueckt[2];
  const vorn: Stueck[] = eingerueckt[1] ? [{ art: 'normal', wert: eingerueckt[1] }] : [];

  if (rumpf.startsWith('#')) return [...vorn, { art: 'kommentar', wert: rumpf }];
  if (!rumpf) return vorn;

  // Listenstrich gehört zum Aufbau, nicht zum Wert.
  let rest = rumpf;
  const strich = /^-\s+/.exec(rest);
  if (sprache === 'yaml' && strich) {
    vorn.push({ art: 'zeichen', wert: strich[0] });
    rest = rest.slice(strich[0].length);
    if (rest.startsWith('#')) return [...vorn, { art: 'kommentar', wert: rest }];
  }

  const trenner = sprache === 'yaml' ? /^([^\s:#][^:#]*?)(:)(\s|$)/ : /^([A-Za-z_][A-Za-z0-9_]*)(=)/;
  const t = trenner.exec(rest);
  if (!t) return [...vorn, ...wertStuecke(rest)];

  vorn.push({ art: 'schluessel', wert: t[1] });
  vorn.push({ art: 'zeichen', wert: t[2] });
  let wert = rest.slice(t[1].length + t[2].length);

  // Angehängter Kommentar. Nur nach einem Leerzeichen und nur außerhalb von
  // Anführungszeichen — `image: x#y` ist kein Kommentar.
  let kommentar = '';
  let inAnfuehrung: string | null = null;
  for (let i = 0; i < wert.length; i++) {
    const c = wert[i];
    if (inAnfuehrung) {
      if (c === inAnfuehrung) inAnfuehrung = null;
      continue;
    }
    if (c === '"' || c === "'") inAnfuehrung = c;
    else if (c === '#' && (i === 0 || /\s/.test(wert[i - 1]))) {
      kommentar = wert.slice(i);
      wert = wert.slice(0, i);
      break;
    }
  }

  const raus = [...vorn, ...wertStuecke(wert)];
  if (kommentar) raus.push({ art: 'kommentar', wert: kommentar });
  return raus;
}

export default function CodeFeld({
  wert,
  onChange,
  sprache,
  zeilen = 16,
  marken = [],
  platzhalter,
}: {
  wert: string;
  onChange: (w: string) => void;
  sprache: Sprache;
  zeilen?: number;
  marken?: ZeilenMarke[];
  platzhalter?: string;
}) {
  const eingabe = useRef<HTMLTextAreaElement>(null);
  const anzeige = useRef<HTMLPreElement>(null);
  const leiste = useRef<HTMLDivElement>(null);

  const alleZeilen = useMemo(() => wert.split('\n'), [wert]);

  const markeJeZeile = useMemo(() => {
    const m = new Map<number, ZeilenMarke>();
    for (const x of marken) {
      // Fehler verdrängt einen Hinweis auf derselben Zeile.
      const da = m.get(x.zeile);
      if (!da || (da.schwere === 'hinweis' && x.schwere === 'fehler')) m.set(x.zeile, x);
    }
    return m;
  }, [marken]);

  // Bildlauf gleichziehen. Ohne das rutscht die Einfärbung weg, sobald jemand
  // scrollt — und dann steht die Farbe auf der falschen Zeile.
  useEffect(() => {
    const feld = eingabe.current;
    if (!feld) return;
    const mit = () => {
      if (anzeige.current) {
        anzeige.current.scrollTop = feld.scrollTop;
        anzeige.current.scrollLeft = feld.scrollLeft;
      }
      if (leiste.current) leiste.current.scrollTop = feld.scrollTop;
    };
    feld.addEventListener('scroll', mit);
    return () => feld.removeEventListener('scroll', mit);
  }, []);

  // Ein gemeinsames Maß für beide Ebenen. Steht hier und nicht zweimal in den
  // Klassen, damit es nicht auseinanderlaufen kann.
  const massEinheitlich: React.CSSProperties = {
    fontFamily:
      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", monospace',
    fontSize: '12px',
    lineHeight: '20px',
    tabSize: 2,
    padding: '8px 10px',
    whiteSpace: 'pre',
    overflowWrap: 'normal',
    margin: 0,
    border: 0,
  };

  const hoehe = `${zeilen * 20 + 16}px`;

  return (
    <div className="overflow-hidden rounded-lg border border-slate-300 bg-surface focus-within:ring-2 focus-within:ring-brand-500">
      <div className="flex" style={{ height: hoehe }}>
        {/* Zeilennummern. Tragen die Befunde des Prüfers — an der Zeile, auf
            die sie sich beziehen, statt in einer Liste darunter. */}
        <div
          ref={leiste}
          className="shrink-0 select-none overflow-hidden border-r border-slate-200 bg-slate-50 text-right"
          style={{ ...massEinheitlich, padding: '8px 6px', width: '3.2rem' }}
        >
          {alleZeilen.map((_, i) => {
            const m = markeJeZeile.get(i + 1);
            return (
              <div
                key={i}
                title={m?.text}
                className={
                  m?.schwere === 'fehler'
                    ? 'bg-red-100 font-medium text-red-700'
                    : m
                      ? 'bg-amber-50 text-amber-700'
                      : 'text-slate-300'
                }
              >
                {i + 1}
              </div>
            );
          })}
        </div>

        <div className="relative min-w-0 flex-1">
          <pre
            ref={anzeige}
            aria-hidden="true"
            className="pointer-events-none absolute inset-0 overflow-hidden"
            style={massEinheitlich}
          >
            {alleZeilen.map((z, i) => {
              const m = markeJeZeile.get(i + 1);
              return (
                <div
                  key={i}
                  className={
                    m?.schwere === 'fehler'
                      ? 'bg-red-50'
                      : m
                        ? 'bg-amber-50/60'
                        : undefined
                  }
                >
                  {zeileZerteilen(z, sprache).map((s, j) => (
                    <span
                      key={j}
                      className={
                        sprache === 'sql' && s.art === 'schluessel' ? FARBE_WORT : FARBE[s.art]
                      }
                    >
                      {s.wert}
                    </span>
                  ))}
                  {/* Leere Zeilen brauchen Höhe, sonst verrutscht alles darunter. */}
                  {z === '' ? '​' : ''}
                </div>
              );
            })}
          </pre>

          <textarea
            ref={eingabe}
            value={wert}
            onChange={(e) => onChange(e.target.value)}
            spellCheck={false}
            autoCapitalize="off"
            autoCorrect="off"
            placeholder={platzhalter}
            className="absolute inset-0 h-full w-full resize-none bg-transparent text-transparent caret-slate-900 outline-none placeholder:text-slate-400"
            style={massEinheitlich}
          />
        </div>
      </div>
    </div>
  );
}
